/**
 * The launch dashboard.
 *
 * Three kinds of number sit on this screen and they are deliberately separated,
 * because mixing them is how an activation rate ends up being computed over a
 * fortnight: *audience* is a snapshot of the account table right now, *reach* looks
 * back a fixed 7 and 30 days from today, and only *engagement* honours the picker.
 *
 * Sections are stacked rather than put behind top-level tabs: in this panel `Tabs`
 * means "a facet over a table", and reusing it for page navigation would break that
 * reading everywhere else.
 */

import {
  EmptyState,
  ErrorState,
  LoadingState,
  StatTile,
  WithHalfdot,
} from "@elyxeer/ui";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { BreakdownBars } from "~/components/analytics/BreakdownBars";
import { DateRangeControl } from "~/components/analytics/DateRangeControl";
import { TrendChart } from "~/components/analytics/TrendChart";
import { Screen } from "~/components/ui/Screen";
import { NO_VALUE, useLocale } from "~/i18n";
import {
  DEFAULT_PRESET,
  isDateKey,
  isPreset,
  type RangePreset,
  resolveRange,
} from "~/lib/analytics-range";
import {
  fetchAnalyticsContent,
  fetchAnalyticsOverview,
  fetchAnalyticsTaxonomy,
  fetchAnalyticsTimeseries,
} from "~/lib/api";
import { downloadCsv, toCsv } from "~/lib/csv";
import { queryKeys } from "~/lib/query-client";
import type { AnalyticsSubjectType } from "~/types/analytics-admin";

const SUBJECT_TYPES: AnalyticsSubjectType[] = ["news", "event", "highlight"];
/** Fetched fresh for the export, so the file does not depend on scroll position. */
const EXPORT_LIMIT = 1000;
const TABLE_LIMIT = 50;

export function AnalyticsRoute() {
  const { t } = useTranslation();
  const { formatDate, formatDuration, formatNumber, formatPercent } =
    useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const presetParam = searchParams.get("range");
  const preset: RangePreset = isPreset(presetParam)
    ? presetParam
    : DEFAULT_PRESET;
  const customFrom = searchParams.get("from");
  const customTo = searchParams.get("to");
  const range = useMemo(
    () =>
      resolveRange(preset, {
        from: isDateKey(customFrom) ? customFrom : undefined,
        to: isDateKey(customTo) ? customTo : undefined,
      }),
    [preset, customFrom, customTo],
  );

  const subjectParam = searchParams.get("type");
  const subjectType: AnalyticsSubjectType = SUBJECT_TYPES.includes(
    subjectParam as AnalyticsSubjectType,
  )
    ? (subjectParam as AnalyticsSubjectType)
    : "news";

  const setParam = (next: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(next)) {
      if (value === null) params.delete(key);
      else params.set(key, value);
    }
    // Replace rather than push: flicking between windows is reading one screen.
    setSearchParams(params, { replace: true });
  };

  const scope = { from: range.from, to: range.to, locale: undefined };

  const overview = useQuery({
    queryKey: queryKeys.analyticsOverview(scope),
    queryFn: ({ signal }) => fetchAnalyticsOverview({ ...range, signal }),
  });
  const timeseries = useQuery({
    queryKey: queryKeys.analyticsTimeseries(scope),
    queryFn: ({ signal }) => fetchAnalyticsTimeseries({ ...range, signal }),
  });
  const content = useQuery({
    queryKey: queryKeys.analyticsContent({
      ...scope,
      subjectType,
      format: undefined,
      topic: undefined,
    }),
    queryFn: ({ signal }) =>
      fetchAnalyticsContent({
        ...range,
        subjectType,
        limit: TABLE_LIMIT,
        signal,
      }),
  });
  const taxonomy = useQuery({
    queryKey: queryKeys.analyticsTaxonomy(scope),
    queryFn: ({ signal }) => fetchAnalyticsTaxonomy({ ...range, signal }),
  });

  const audience = overview.data?.audience;
  const current = overview.data?.current;
  const previous = overview.data?.previous;

  /** A signed percentage change, or undefined when there is nothing to compare to. */
  const delta = (
    now: number | null | undefined,
    before: number | null | undefined,
    labelKey: string,
  ) => {
    if (now === null || now === undefined) return undefined;
    if (before === null || before === undefined || before === 0)
      return undefined;
    const percent = ((now - before) / before) * 100;
    return {
      percent,
      label: t(labelKey, { percent: Math.round(percent) }),
    };
  };

  const exportContent = async () => {
    const page = await fetchAnalyticsContent({
      ...range,
      subjectType,
      limit: EXPORT_LIMIT,
    });
    const header = [
      t("analytics.content.columns.title"),
      t("analytics.content.columns.slug"),
      t("analytics.content.columns.format"),
      t("analytics.content.columns.published"),
      t("analytics.content.columns.views"),
      t("analytics.content.columns.unique"),
      t("analytics.content.columns.medianTime"),
      t("analytics.content.columns.readThrough"),
      t("analytics.content.columns.videoCompletion"),
    ];
    const rows = page.items.map((row) => [
      row.title ?? row.subject_key,
      row.subject_key,
      row.format ?? "",
      row.published_at ?? "",
      row.views,
      row.unique_viewers,
      row.median_dwell_seconds ?? "",
      row.completion_rate ?? "",
      row.video_starts === 0 ? "" : row.video_q4 / row.video_starts,
    ]);
    downloadCsv(
      t("analytics.exportFilename", { from: range.from, to: range.to }),
      toCsv([header, ...rows]),
    );
  };

  return (
    <Screen maxWidth="xl">
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
          marginBottom: 3,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            <WithHalfdot size={11}>{t("analytics.title")}</WithHalfdot>
          </Typography>
          <Typography
            variant="T2"
            sx={{ color: "text.secondary", marginTop: 1 }}
          >
            {t("analytics.subtitle")}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={() => void exportContent()}
          disabled={(content.data?.items.length ?? 0) === 0}
          data-testid="analytics.export"
        >
          {t("analytics.export")}
        </Button>
      </Box>

      <Box sx={{ marginBottom: 4 }}>
        <DateRangeControl
          preset={preset}
          range={range}
          onPresetChange={(next) =>
            setParam(
              next === "custom"
                ? { range: next, from: range.from, to: range.to }
                : { range: next, from: null, to: null },
            )
          }
          onRangeChange={(next) =>
            setParam({ range: "custom", from: next.from, to: next.to })
          }
        />
      </Box>

      {overview.isPending ? <LoadingState label={t("common.loading")} /> : null}
      {overview.isError ? (
        <ErrorState
          message={t("analytics.error")}
          onRetry={() => void overview.refetch()}
        />
      ) : null}

      {audience && current ? (
        <>
          <Typography variant="H2" sx={{ marginBottom: 2 }}>
            {t("analytics.sections.activation")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: 2,
              marginBottom: 5,
            }}
          >
            <StatTile
              label={t("analytics.tiles.withAccess")}
              value={formatNumber(audience.with_access)}
              caption={t("analytics.tiles.withAccessCaption")}
              testId="analytics.tile-with-access"
            />
            <StatTile
              label={t("analytics.tiles.activated")}
              value={formatNumber(audience.activated)}
              caption={t("analytics.tiles.activatedCaption")}
              testId="analytics.tile-activated"
            />
            <StatTile
              label={t("analytics.tiles.activationRate")}
              value={formatPercent(audience.activation_rate)}
              caption={t("analytics.tiles.activationRateCaption")}
              testId="analytics.tile-activation-rate"
            />
            <StatTile
              label={t("analytics.tiles.neverSignedIn")}
              value={formatNumber(audience.never_signed_in)}
              caption={t("analytics.tiles.neverSignedInCaption")}
              testId="analytics.tile-never-signed-in"
            />
          </Box>

          <Typography variant="H2" sx={{ marginBottom: 2 }}>
            {t("analytics.sections.engagement")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: 2,
              marginBottom: 4,
            }}
          >
            <StatTile
              label={t("analytics.tiles.active7d")}
              value={formatNumber(overview.data?.active_7d ?? 0)}
              caption={t("analytics.tiles.rollingCaption")}
              testId="analytics.tile-active-7d"
            />
            <StatTile
              label={t("analytics.tiles.active30d")}
              value={formatNumber(overview.data?.active_30d ?? 0)}
              caption={t("analytics.tiles.rollingCaption")}
              testId="analytics.tile-active-30d"
            />
            <StatTile
              label={t("analytics.tiles.sessions")}
              value={formatNumber(current.sessions)}
              delta={delta(
                current.sessions,
                previous?.sessions,
                "analytics.delta.vsPrevious",
              )}
              caption={previous ? undefined : t("analytics.delta.noComparison")}
              testId="analytics.tile-sessions"
            />
            <StatTile
              label={t("analytics.tiles.returnRate")}
              value={formatPercent(current.return_rate)}
              caption={t("analytics.tiles.returnRateCaption")}
              testId="analytics.tile-return-rate"
            />
            <StatTile
              label={t("analytics.tiles.contentViews")}
              value={formatNumber(current.content_views)}
              delta={delta(
                current.content_views,
                previous?.content_views,
                "analytics.delta.vsPrevious",
              )}
              testId="analytics.tile-content-views"
            />
            <StatTile
              label={t("analytics.tiles.uniqueViewers")}
              value={formatNumber(current.unique_content_viewers)}
              testId="analytics.tile-unique-viewers"
            />
            <StatTile
              label={t("analytics.tiles.medianTime")}
              value={formatDuration(current.median_content_seconds)}
              caption={t("analytics.tiles.medianTimeCaption")}
              testId="analytics.tile-median-time"
            />
            <StatTile
              label={t("analytics.tiles.chatMessages")}
              value={formatNumber(current.chat_messages)}
              caption={t("analytics.tiles.chatMessagesCaption", {
                count: current.chat_members,
              })}
              testId="analytics.tile-chat-messages"
            />
          </Box>

          <Paper
            variant="outlined"
            sx={{ padding: { xs: 2, md: 3 }, marginBottom: 5 }}
          >
            <Typography variant="H3" sx={{ marginBottom: 2 }}>
              {t("analytics.trend.title")}
            </Typography>
            {timeseries.data && timeseries.data.items.length > 0 ? (
              <TrendChart
                points={timeseries.data.items.map((point) => ({
                  day: point.day,
                  value: point.sessions,
                }))}
                label={t("analytics.trend.ariaLabel", {
                  from: formatDate(range.from),
                  to: formatDate(range.to),
                  peak: Math.max(
                    0,
                    ...timeseries.data.items.map((point) => point.sessions),
                  ),
                })}
                testId="analytics.trend"
              />
            ) : (
              <EmptyState message={t("analytics.trend.empty")} />
            )}
          </Paper>
        </>
      ) : null}

      <Typography variant="H2" sx={{ marginBottom: 2 }}>
        {t("analytics.sections.content")}
      </Typography>
      <Tabs
        value={subjectType}
        onChange={(_event, next: AnalyticsSubjectType) =>
          setParam({ type: next })
        }
        sx={{ marginBottom: 2 }}
        aria-label={t("analytics.content.typeLabel")}
      >
        {SUBJECT_TYPES.map((option) => (
          <Tab
            key={option}
            value={option}
            label={t(`analytics.content.type.${option}`)}
            data-testid={`analytics.content-tab-${option}`}
          />
        ))}
      </Tabs>

      {content.isPending ? <LoadingState label={t("common.loading")} /> : null}
      {content.isError ? (
        <ErrorState
          message={t("analytics.error")}
          onRetry={() => void content.refetch()}
        />
      ) : null}
      {content.data && content.data.items.length === 0 ? (
        <EmptyState message={t("analytics.content.empty")} />
      ) : null}

      {content.data && content.data.items.length > 0 ? (
        <Table
          size="small"
          data-testid="analytics.content-table"
          sx={{ marginBottom: 5 }}
        >
          <TableHead>
            <TableRow>
              <TableCell>{t("analytics.content.columns.title")}</TableCell>
              <TableCell align="right">
                {t("analytics.content.columns.views")}
              </TableCell>
              <TableCell align="right">
                {t("analytics.content.columns.unique")}
              </TableCell>
              <TableCell align="right">
                {t("analytics.content.columns.medianTime")}
              </TableCell>
              <TableCell align="right">
                {t("analytics.content.columns.readThrough")}
              </TableCell>
              <TableCell align="right">
                {t("analytics.content.columns.videoCompletion")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {content.data.items.map((row) => (
              <TableRow
                key={row.subject_key}
                data-testid={`analytics.content-row-${row.subject_key}`}
              >
                <TableCell>
                  <Typography variant="T3">
                    {row.title ?? row.subject_key}
                  </Typography>
                  {row.format ? (
                    <Typography variant="L3" sx={{ color: "text.muted" }}>
                      {t(`formats.${row.format}`)}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right">{formatNumber(row.views)}</TableCell>
                <TableCell align="right">
                  {formatNumber(row.unique_viewers)}
                </TableCell>
                <TableCell align="right">
                  {formatDuration(row.median_dwell_seconds)}
                </TableCell>
                <TableCell align="right">
                  {formatPercent(row.completion_rate)}
                </TableCell>
                <TableCell align="right">
                  {row.video_starts === 0
                    ? NO_VALUE
                    : formatPercent(row.video_q4 / row.video_starts)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          gap: 4,
        }}
      >
        <BreakdownBars
          title={t("analytics.breakdown.topics")}
          rows={(taxonomy.data?.topics ?? []).slice(0, 8).map((row) => ({
            key: row.key,
            label: t(`topics.${row.key}`, { defaultValue: row.key }),
            value: row.views,
          }))}
          emptyMessage={t("analytics.breakdown.empty")}
          formatValue={formatNumber}
          testId="analytics.breakdown-topics"
        />
        <BreakdownBars
          title={t("analytics.breakdown.formats")}
          rows={(taxonomy.data?.formats ?? []).map((row) => ({
            key: row.key,
            label: t(`formats.${row.key}`, { defaultValue: row.key }),
            value: row.views,
          }))}
          emptyMessage={t("analytics.breakdown.empty")}
          formatValue={formatNumber}
          testId="analytics.breakdown-formats"
        />
      </Box>
    </Screen>
  );
}
